import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
// The package content styles are shared with the old modal rather than duplicated, so a package
// looks the same wherever it is described.
import "../../components/PackagePurchaseModal.css";
import "./PackagePurchasePage.css";
import {
  PACKAGE_TERMS_PATH,
  PACKAGE_TERMS_SECTIONS,
  PACKAGE_TERMS_SUMMARY,
  PACKAGE_TERMS_VERSION,
} from "../../content/packageTerms";

/**
 * Buying a package — a PAGE, not a modal (Phase 15).
 *
 * Why this is a page and not a modal:
 *   - a purchase is linkable, refreshable and shareable, and survives the trip to Stripe and back;
 *   - it can carry the buyer through more than one step without fighting a dialog's scroll;
 *   - a modal cannot be reopened by URL, so an abandoned purchase had to be restarted from the
 *     shop page every time.
 *
 * It must stay reachable WITHOUT an account. A guest buying a package is the whole reason package
 * purchase uses `optionalAuthenticateToken`, so this route is deliberately outside ProtectedRoute.
 *
 * The three details collected here — first name, email, phone — are not decoration:
 *   - Phase 12 sends SMS reminders to the phone;
 *   - Phase 18 uses the phone as a guest's second identity factor when redeeming;
 *   - Phase 16 writes them onto the booking the purchase can create;
 *   - Phase 17 shows them to the shop.
 * A guest has no account to read them back from, so this is the only chance to collect them.
 *
 * CLIENT VALIDATION IS A COURTESY, NOT THE RULE. The server applies the same requirements in
 * `utils/packageBuyerDetails.js` and refuses a payload that omits them — the endpoint is reachable
 * directly, and this repo has already been burned once by treating a hidden control as enforcement.
 */

const API_BASE = String(process.env.REACT_APP_API_BASE_URL || "").replace(
  /\/+$/,
  "",
);

// Mirrors `isValidEmail` in utils/securityHelpers.js.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (value) =>
  typeof value === "string" &&
  EMAIL_PATTERN.test(value) &&
  value.length <= 254;

// Mirrors `isValidPhone` in utils/securityHelpers.js: formatting characters are stripped, then
// 10–15 digits with an optional leading `+`.
const PHONE_FORMATTING = /[\s\-().]/g;
const PHONE_DIGITS = /^\+?\d{10,15}$/;
const isValidPhone = (value) =>
  typeof value === "string" && PHONE_DIGITS.test(value.replace(PHONE_FORMATTING, ""));
const PackagePurchasePage = () => {
  const { packageId } = useParams();
  const [searchParams] = useSearchParams();

  const [pkg, setPkg] = useState(null);
  const [sellability, setSellability] = useState(null);
  const [subscribedProfessionals, setSubscribedProfessionals] = useState([]);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | missing
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // The copy button reports its own success. It previously used `alert()`, which is a modal the
  // buyer has to dismiss for a link they will paste somewhere else — it interrupted the one action
  // that needed no attention.
  const [copied, setCopied] = useState(false);
  /**
   * THE TERMS, AND THE ACCEPTANCE (user, 2026-10-14).
   *
   * `termsOpen` shows the clauses inline on this page, so a buyer does not have to leave the form they were
   * filling in (and lose it) to read what they are agreeing to. The link to the full page is offered too, for
   * anybody who wants it in its own window.
   *
   * `termsAccepted` is NOT pre-ticked, and the server refuses the purchase without it.
   */
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState("");

  const [form, setForm] = useState({
    firstName: "",
    // Optional. Present because a booking and a receipt read better with a full name; NOT required,
    // and the server does not require it either.
    lastName: "",
    customerEmail: "",
    customerPhone: "",
  });

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("ogestyle_token")
      : null;
  const isGuest = !token;

  const shopId = useMemo(
    () => searchParams.get("shopId") || pkg?.availableAtShops?.[0] || "",
    [searchParams, pkg],
  );

  const update = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  /**
   * A signed-in buyer should not retype what the platform already holds. This is a CONVENIENCE
   * only: the server fills any gap from the account too, and an explicit value here still wins —
   * so a stale phone number can be corrected at the moment of purchase.
   */
  const prefillFromAccount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const user = data?.user;
      if (!user) return;
      setForm((prev) => ({
        firstName: prev.firstName || user.firstName || "",
        lastName: prev.lastName || user.lastName || "",
        customerEmail: prev.customerEmail || user.email || "",
        customerPhone: prev.customerPhone || user.phone || "",
      }));
    } catch {
      // A missing profile is not a reason to block the purchase: the fields are simply blank and
      // the buyer fills them in, which is exactly what a guest does.
    }
  }, [token]);

  const loadPackage = useCallback(async () => {
    if (!packageId) {
      setLoadState("missing");
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/packages/${packageId}`);
      if (!res.ok) {
        setLoadState("missing");
        return;
      }
      const data = await res.json();
      if (!data?.package) {
        setLoadState("missing");
        return;
      }
      setPkg(data.package);
      setSellability(data.sellability || null);
      setLoadState("ready");
    } catch {
      setLoadState("missing");
    }
  }, [packageId]);

  useEffect(() => {
    loadPackage();
    prefillFromAccount();
  }, [loadPackage, prefillFromAccount]);

  /**
   * "Accepted by" — read from the PUBLIC shop listing with `customerView=true`. That route returns
   * resolved names for everyone; the package detail route returns bare ids. Requesting the buyer's
   * view explicitly also means this page never asks for the shop's business figures.
   */
  useEffect(() => {
    const load = async () => {
      const locationId = pkg?.availableAtShops?.[0];
      if (!locationId) return;
      try {
        const res = await fetch(
          `${API_BASE}/api/packages/shop/${locationId}?customerView=true`,
        );
        if (!res.ok) return;
        const data = await res.json();
        const match = (data.packages || []).find(
          (entry) => String(entry._id) === String(pkg._id),
        );
        setSubscribedProfessionals(
          (match?.subscriberDetails || []).map((entry) => ({
            _id: entry.staffId,
            name: entry.name || "Professional",
            // The headshot identifies the professional better than a name — and the route already
            // returns it, so it costs nothing to use.
            profilePicture: entry.profilePicture || null,
            // What this professional can deliver of THIS package. `coversWholePackage` is null when
            // the API could not work it out — an unknown must not be rendered as "partial".
            coveredServices: entry.coveredServices || [],
            coversWholePackage:
              typeof entry.coversWholePackage === "boolean"
                ? entry.coversWholePackage
                : null,
          })),
        );
      } catch {
        // Names are a nicety; never block a purchase on them.
      }
    };
    load();
  }, [pkg]);

  const validate = () => {
    const errors = {};

    if (!form.firstName.trim()) {
      errors.firstName = "A first name is required to purchase a package.";
    }
    if (!form.customerEmail.trim()) {
      errors.customerEmail =
        "An email address is required to purchase a package.";
    } else if (!isValidEmail(form.customerEmail.trim().toLowerCase())) {
      errors.customerEmail =
        "Please enter a valid email address, for example name@example.com.";
    }
    if (!form.customerPhone.trim()) {
      errors.customerPhone = "A phone number is required to purchase a package.";
    } else if (!isValidPhone(form.customerPhone.trim())) {
      errors.customerPhone =
        "Please enter a valid phone number, including the country code if you are outside the US.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * THE TERMS ARE A REQUIREMENT, NOT A LINK IN THE FOOTER (user, 2026-10-14).
   *
   * Checked HERE so the customer is told before a request is made, and checked AGAIN server-side, because this
   * form is not the gate — the API is. The version travels with the purchase and is stored on it, which is what
   * makes the acceptance provable afterwards; the server refuses a version that is not current, so a page left
   * open across a terms change cannot accept wording the customer never saw.
   */
  const validateTerms = () => {
    if (!termsAccepted) {
      setTermsError(
        "Please read and accept the Package Terms before buying.",
      );
      return false;
    }
    setTermsError("");
    return true;
  };

  const handlePurchase = async () => {
    setError("");
    if (!validate()) {
      return;
    }
    if (!validateTerms()) {
      return;
    }

    setSubmitting(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_BASE}/api/packages/${pkg._id}/checkout`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            shopId,
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            customerEmail: form.customerEmail.trim(),
            customerPhone: form.customerPhone.trim(),
            // The acceptance, and WHICH text it was.
            termsAccepted: termsAccepted === true,
            termsVersion: PACKAGE_TERMS_VERSION,
            // WHERE TO PUT THEM AFTERWARDS (2026-10-26). Set when the purchase was started from inside a
            // booking, so the confirmation page can hand them back to the booking they were filling in. The
            // server validates it as an internal path before it becomes a Stripe return URL.
            returnTo: searchParams.get("returnTo") || "",
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        // Terms refusals come back with a version or acceptance problem; show them at the tick rather than
        // above the form, and reopen the terms so the customer can read the current text.
        if (String(data.error || "").startsWith("terms_")) {
          setTermsError(data.message || "Please read and accept the Package Terms.");
          setTermsOpen(true);
          throw new Error("");
        }
        // The server names the field it refused, so the message lands on the input that caused it
        // instead of floating above a form with nothing to act on.
        if (data.field) {
          setFieldErrors((prev) => ({ ...prev, [data.field]: data.message }));
        }
        throw new Error(
          data.message || data.error || "Could not start the purchase.",
        );
      }

      if (data.stripeCheckoutUrl) {
        window.location.href = data.stripeCheckoutUrl;
        return;
      }

      throw new Error(
        "The payment page could not be opened. Please try again.",
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // `validityPeriod: { value, unit }` — the same shape the checkout route applies.
  const validityPeriod = useMemo(() => {
    const value = Number(pkg?.validityPeriod?.value);
    if (!Number.isFinite(value) || value <= 0) return null;
    return { value, unit: pkg.validityPeriod.unit || "days" };
  }, [pkg]);

  const expiryDate = useMemo(() => {
    if (!validityPeriod) return null;
    const date = new Date();
    switch (validityPeriod.unit) {
      case "weeks":
        date.setDate(date.getDate() + validityPeriod.value * 7);
        break;
      case "months":
        date.setMonth(date.getMonth() + validityPeriod.value);
        break;
      case "years":
        date.setFullYear(date.getFullYear() + validityPeriod.value);
        break;
      default:
        date.setDate(date.getDate() + validityPeriod.value);
    }
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, [validityPeriod]);

  const isExpiringSoon = useMemo(() => {
    if (!validityPeriod) return false;
    const date = new Date();
    switch (validityPeriod.unit) {
      case "weeks":
        date.setDate(date.getDate() + validityPeriod.value * 7);
        break;
      case "months":
        date.setMonth(date.getMonth() + validityPeriod.value);
        break;
      case "years":
        date.setFullYear(date.getFullYear() + validityPeriod.value);
        break;
      default:
        date.setDate(date.getDate() + validityPeriod.value);
    }
    return Math.ceil((date - Date.now()) / (1000 * 60 * 60 * 24)) < 60;
  }, [validityPeriod]);

  const notSellable =
    sellability && sellability.sellable === false
      ? sellability.messages?.[0] || "This package is not available right now."
      : null;

  if (loadState === "loading") {
    return (
      <>
        <CustomerHeader />
        <div className="package-page-shell">
          <div className="package-page-loading">Loading package…</div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (loadState === "missing" || !pkg) {
    return (
      <>
        <CustomerHeader />
        <div className="package-page-shell">
          <div className="package-page-missing">
            <h2>Package not found</h2>
            <p>
              This package may have been removed, or the link may be incomplete.
            </p>
            <Link to="/packages">Browse packages</Link>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  const savings =
    Number(pkg.regularPrice) > Number(pkg.packagePrice)
      ? Number(pkg.regularPrice) - Number(pkg.packagePrice)
      : 0;

  return (
    <>
      <CustomerHeader />
      <div className="package-page-shell">
        <div className="package-page-content">
          <div className="package-modal-header">
            <h2>{pkg.name}</h2>
            {savings > 0 && (
              <div className="package-badge-savings">
                💰 Save ${savings.toFixed(2)}
              </div>
            )}
          </div>

          <div className="package-modal-body">
            {notSellable && (
              <div className="package-error">
                This package cannot be purchased right now: {notSellable}
              </div>
            )}

            {/* Hidden when the description is just the name typed in twice — which is how many of
                these packages were created, and it made the name appear twice in a row. */}
            {pkg.description && pkg.description.trim() !== pkg.name?.trim() && (
              <p className="package-description">{pkg.description}</p>
            )}

            <div className="package-price-section">
              <div className="package-price-display">
                <span className="package-price-current">
                  ${pkg.packagePrice}
                </span>
                {savings > 0 && (
                  <span className="package-price-original">
                    ${pkg.regularPrice}
                  </span>
                )}
              </div>
              <div className="package-value-label">
                {pkg.totalSessions} sessions included
              </div>
            </div>

            {pkg.services && pkg.services.length > 0 && (
              <div className="package-services-section">
                <h4>Included Services:</h4>
                <ul className="package-services-list">
                  {pkg.services.map((service, idx) => (
                    <li key={idx}>
                      <span className="service-icon">✓</span>{" "}
                      {service.serviceId?.name || service.serviceName}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {subscribedProfessionals.length > 0 && (
              <div className="package-professionals-section">
                <h4>Accepted By:</h4>
                <div className="package-professionals-list">
                  {subscribedProfessionals.map((pro) => (
                    <div key={pro._id} className="package-professional-chip">
                      {/* A headshot, because a name alone does not tell a customer who they will be
                          booking with. The initial is the fallback for a professional with no photo. */}
                      {pro.profilePicture ? (
                        <img
                          className="package-professional-avatar"
                          src={pro.profilePicture}
                          alt=""
                          loading="lazy"
                        />
                      ) : (
                        <span className="package-professional-avatar package-professional-avatar-empty">
                          {String(pro.name || "?").charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span>
                        {pro.name}
                        {/* A professional who offers only PART of the bundle must not look like one
                            who covers all of it. Named, because "partial" alone does not tell the
                            buyer which sessions they can actually book. */}
                        {pro.coversWholePackage === false &&
                          pro.coveredServices.length > 0 && (
                            <span className="package-professional-scope">
                              {" — "}
                              {pro.coveredServices.join(", ")} only
                            </span>
                          )}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="package-professionals-note">
                  Book each included service with a professional who offers it —
                  some professionals cover only part of this package.
                </p>
              </div>
            )}

            {/* ONE compact row instead of three stacked blocks. The facts are unchanged; the page
                was simply too tall to scan. A buyer deciding whether to pay reads three short facts,
                not three cards. */}
            <div className="package-facts-row">
              {validityPeriod && (
                <span className="package-fact">
                  📅 Valid until {expiryDate}
                </span>
              )}
              <span className="package-fact">🔒 Non-refundable</span>
              <span className="package-fact">💳 One-time payment via Stripe</span>
            </div>

            {isExpiringSoon && (
              <div className="package-warning">
                ⚠️ This package expires in less than 60 days — please make sure
                you can use it in time.
              </div>
            )}

            {/* Required for EVERY buyer, guest or signed-in. A signed-in buyer's details arrive
                pre-filled, but they are still shown: what will be used should be visible and
                correctable rather than read silently from an account. */}
            <div className="package-buyer-details">
              <h4>Your details</h4>

              {/* First and last name share a row: the last name is optional, and giving it a row of
                  its own would lengthen the page for a field that is not required. */}
              <div className="package-field-row">
                <div>
                  <label htmlFor="package-buyer-first-name">First name</label>
                  <input
                    id="package-buyer-first-name"
                    type="text"
                    value={form.firstName}
                    onChange={update("firstName")}
                    autoComplete="given-name"
                    required
                  />
                  {fieldErrors.firstName && (
                    <div className="package-field-error">
                      {fieldErrors.firstName}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="package-buyer-last-name">
                    Last name{" "}
                    <span className="package-optional">(optional)</span>
                  </label>
                  <input
                    id="package-buyer-last-name"
                    type="text"
                    value={form.lastName}
                    onChange={update("lastName")}
                    autoComplete="family-name"
                  />
                </div>
              </div>

              {/* Email and phone sit side by side: two short fields, one row of height. */}
              <div className="package-field-row">
                <div>
                  <label htmlFor="package-buyer-email">Email</label>
                  <input
                    id="package-buyer-email"
                    type="email"
                    value={form.customerEmail}
                    onChange={update("customerEmail")}
                    autoComplete="email"
                    required
                  />
                  {fieldErrors.customerEmail && (
                    <div className="package-field-error">
                      {fieldErrors.customerEmail}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="package-buyer-phone">Phone</label>
                  <input
                    id="package-buyer-phone"
                    type="tel"
                    value={form.customerPhone}
                    onChange={update("customerPhone")}
                    autoComplete="tel"
                    required
                  />
                  {fieldErrors.customerPhone && (
                    <div className="package-field-error">
                      {fieldErrors.customerPhone}
                    </div>
                  )}
                </div>
              </div>

              {/* One note where there were three. It says the two things that were each repeated:
                  no account is needed, and neither detail is public. */}
              <div className="package-guest-email-note">
                No account needed. Your confirmation goes to this email, and the
                phone is used for booking reminders and to identify you when you
                redeem. Neither is shown publicly.
                {isGuest
                  ? " You can save this to an account later if you want."
                  : ""}
              </div>
            </div>

            {/* ── THE TERMS, ON THE PAGE THAT TAKES THE MONEY (user, 2026-10-14) ─────────────────────

                *"I can't see the T&C being shown to the customers/guests when purchasing packages, this is
                really important if not we can't enforce any of these on the customer/guest."*

                The summary is always visible, so the parts that matter are read even by somebody who will not
                open the full text. The clauses open INLINE rather than only as a link, because navigating away
                mid-form risks losing what they typed — and the full page is offered beside it for anybody who
                wants it. The version shown here is the version sent with the purchase and the one the server
                checks, so an acceptance can be tied to exact wording. */}
            <div
              style={{
                marginTop: "16px",
                padding: "12px 14px",
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "6px" }}>
                Package terms
              </div>
              <ul style={{ margin: "0 0 8px 0", paddingLeft: "20px", fontSize: "13px", color: "#374151" }}>
                {PACKAGE_TERMS_SUMMARY.map((line) => (
                  <li key={line} style={{ marginBottom: "3px" }}>
                    {line}
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => setTermsOpen((open) => !open)}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "#1d4ed8",
                  fontSize: "13px",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {termsOpen
                  ? "Hide the full terms"
                  : "Read the full terms"}
              </button>{" "}
              <Link
                to={PACKAGE_TERMS_PATH}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: "13px", color: "#1d4ed8", marginLeft: "8px" }}
              >
                Open in a new tab
              </Link>

              {termsOpen && (
                <div
                  style={{
                    marginTop: "10px",
                    maxHeight: "320px",
                    overflowY: "auto",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    background: "white",
                    padding: "12px",
                  }}
                >
                  {PACKAGE_TERMS_SECTIONS.map((section) => (
                    <div key={section.heading} style={{ marginBottom: "12px" }}>
                      <div style={{ fontWeight: 600, fontSize: "13px" }}>{section.heading}</div>
                      {(section.paragraphs || []).map((paragraph) => (
                        <p key={paragraph} style={{ fontSize: "12px", margin: "4px 0", color: "#374151" }}>
                          {paragraph}
                        </p>
                      ))}
                      {section.table && (
                        <ul style={{ margin: "4px 0", paddingLeft: "18px", fontSize: "12px", color: "#374151" }}>
                          {section.table.map(([what, cost]) => (
                            <li key={what} style={{ marginBottom: "3px" }}>
                              <strong>{what}</strong> — {cost}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <label
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "flex-start",
                  marginTop: "10px",
                  fontSize: "13px",
                  color: "#111827",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(event) => {
                    setTermsAccepted(event.target.checked);
                    if (event.target.checked) setTermsError("");
                  }}
                  style={{ marginTop: "2px" }}
                />
                <span>
                  I have read and accept the Package Terms
                  {termsOpen ? "" : " (open them above if you have not)"}.
                </span>
              </label>

              {termsError && (
                <div className="package-field-error" style={{ marginTop: "6px" }}>
                  {termsError}
                </div>
              )}
            </div>

            {error && <div className="package-error">{error}</div>}

            <div className="package-modal-actions">
              <button
                onClick={handlePurchase}
                disabled={submitting || Boolean(notSellable) || !termsAccepted}
                className="package-purchase-btn"
                title={
                  termsAccepted
                    ? undefined
                    : "Accept the Package Terms to continue"
                }
              >
                {submitting
                  ? "Processing..."
                  : `Purchase for $${pkg.packagePrice}`}
              </button>
              <Link className="package-cancel-btn" to={-1}>
                Cancel
              </Link>
            </div>

            {/* What this link IS: the package's own purchase page, which starts a purchase for
                whoever opens it. It is NOT a gift — nothing here transfers a purchase to anyone
                else, so the wording must not imply that it does. The raw URL is hidden: it was the
                tallest thing on the page and nobody reads it, they copy it. */}
            <div className="package-share-section">
              <p className="package-share-label">
                Know someone who wants this? Send them the link — they buy it
                themselves.
              </p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/packages/${pkg._id}/purchase?shopId=${shopId}`,
                  );
                  setCopied(true);
                }}
                className="package-copy-btn"
              >
                {copied ? "✓ Link copied" : "📋 Copy link to this package"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <CustomerFooter />
    </>
  );
};

export default PackagePurchasePage;
